/**
 * Alerts Service
 * Abstraction layer for hot alert management
 */
import { api } from './api'

const today = () => new Date().toISOString().split('T')[0]

const splitDateTime = (value, fallbackDate = today(), fallbackTime = '00:00') => {
    if (!value) return { date: fallbackDate, time: fallbackTime }
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return { date: fallbackDate, time: fallbackTime }

    return {
        date: date.toISOString().split('T')[0],
        time: date.toISOString().slice(11, 16),
    }
}

const mapAlert = (alert) => {
    const start = splitDateTime(alert?.start_at || alert?.created_at)
    const end = splitDateTime(alert?.end_at, '', '23:59')

    return {
        id: alert?.id || null,
        enabled: alert?.enabled || false,
        message: alert?.message || '',
        type: alert?.type || 'service_outage',
        startDate: start.date,
        startTime: start.time,
        endDate: end.date,
        endTime: end.time,
        timezone: alert?.timezone || 'UTC',
        createdAt: alert?.created_at || null,
        updatedAt: alert?.updated_at || null,
        disabledAt: alert?.disabled_at || null,
        createdByName: alert?.created_by_name || '',
        createdByEmail: alert?.created_by_email || '',
    }
}

const defaultAlert = () => ({
    enabled: false,
    message: '',
    type: 'service_outage',
    startDate: today(),
    startTime: '00:00',
    endDate: '',
    endTime: '23:59',
    timezone: 'UTC',
})

export const alertsService = {
    async getHotAlert() {
        try {
            const res = await api.get('/admin/hot-alert')
            if (!res.success) throw new Error('Failed to fetch alert')

            if (res.data) return mapAlert(res.data)
            return defaultAlert()
        } catch (err) {
            console.error('Failed to load hot alert:', err)
            return defaultAlert()
        }
    },

    async getHotAlertHistory() {
        try {
            const res = await api.get('/admin/hot-alert/history')
            if (!res.success) throw new Error('Failed to fetch alert history')
            return (res.data || []).map(mapAlert)
        } catch (err) {
            console.error('Failed to load hot alert history:', err)
            return []
        }
    },

    async updateHotAlert(data) {
        try {
            const res = await api.put('/admin/hot-alert', {
                message: data.message,
                enabled: data.enabled,
                type: data.type,
                startDate: data.startDate,
                startTime: data.startTime,
                endDate: data.endDate,
                endTime: data.endTime,
                timezone: data.timezone,
            })
            if (!res.success) throw new Error('Failed to update alert')
            return res.data ? mapAlert(res.data) : defaultAlert()
        } catch (err) {
            console.error('Failed to update hot alert:', err)
            throw err
        }
    },

    async enableHotAlert() {
        const current = await this.getHotAlert()
        return this.updateHotAlert({ ...current, enabled: true })
    },

    async disableHotAlert() {
        try {
            const res = await api.put('/admin/hot-alert', { enabled: false })
            if (!res.success) throw new Error('Failed to disable alert')
            return defaultAlert()
        } catch (err) {
            console.error('Failed to disable hot alert:', err)
            throw err
        }
    },
}
